import { Colophon } from "../types/colophon";
import { EntityController } from "./entityController";
import { useNavigate } from "react-router-dom";
import useColophonQuery from "../hooks/query/useColophonQuery";
import { extractReferenceKey } from "../components/forms/fields/basic/EntitySelectionField";
import useColophonLogic from "../hooks/logic/colophonLogic";
import useVersionRefLogic from "../hooks/logic/versionRefLogic";

export interface ColophonController extends EntityController<Colophon> {
  displayName: (data: Colophon) => string;
  viewTitle: (data: Colophon) => string;
  editTitle: (data: Colophon) => string;
}

const useColophonController = (id: string | null): ColophonController => {
  const { useFetchById, useUpdate, useCreate, useDeleteById } =
    useColophonQuery();
  const { userEnums, defaultColophon, getDisplayName, metadataFieldNames } =
    useColophonLogic();
  const { getUpdatedVersionReference } = useVersionRefLogic();
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const deleteById = useDeleteById();
  const navigate = useNavigate();

  const fetchData = async (): Promise<Colophon> => {
    if (!id) {
      return defaultColophon;
    }
    const res = await fetchById.refetch();
    const colophonData = res.data;
    if (!colophonData || res.isError) {
      throw new Error("Colophon data not found");
    }
    return colophonData;
  };

  const isCreateMode = id === null;

  const displayName = (data: Colophon) => getDisplayName(data);

  const viewTitle = (data: Colophon) => displayName(data);

  const editTitle = (data: Colophon) =>
    `${isCreateMode ? "Create New" : "Edit"} ${displayName(data)}`;

  const onSubmit = async (data: Partial<Colophon>) => {
    const updateData: Partial<Colophon> = {
      internal_notes: data.internal_notes,
      notes: data.notes,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      version: getUpdatedVersionReference(data.version),
      virtually_identical_with: (
        data.virtually_identical_with?.map((v) =>
          extractReferenceKey(v.version)
        ) || []
      )
        .filter((id): id is string => !!id)
        .map((id) => ({ id })),
      work_id: extractReferenceKey(data.work),
      colophon: data.colophon,
      attributes_metadata: {
        ...defaultColophon.attributes_metadata,
        ...data.attributes_metadata,
      },
      author_id: extractReferenceKey(data.author),
      name_internal_evidence: data.name_internal_evidence,
    };

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...updateData },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update colophon");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create colophon");
      }
    }
    return result;
  };

  const onDelete = async () => {
    if (!id) {
      throw new Error("Colophon ID is required for deletion");
    }
    await deleteById.mutateAsync(id);
    if (deleteById.isError) {
      throw new Error("Failed to delete colophon");
    }
  };

  const navigateBack = () => navigate(-1);

  const deleteWarningMessage = "Are you sure you want to delete this colophon?";

  const getEditRoute = () => `/colophons/${id}/edit`;

  return {
    fetchData,
    userEnums,

    displayName,
    viewTitle,
    editTitle,
    title: viewTitle,

    defaultValues: defaultColophon,
    metadataFieldNames,
    isCreateMode,
    onSubmit,
    onDelete,
    deleteWarningMessage,
    formCallback: navigateBack,

    getEditRoute,
  };
};

export default useColophonController;
